import _ from "lodash";

import { US_DOLLAR } from "lib/frontend/cm/utils/constants";
import { ReviewExchangeRates } from "lib/shared/review/types";

export const Locales = {
  enUS: "en-US",
};

export type CurrencyConvertFn = ({ value, options }: ConvertCurrencyParams) => ConvertedCurrency;

export type ConvertCurrencyParams = {
  value: number;
  options: {
    sourceCurrency: string;
    targetCurrency: string;
    exchangeRates: ReviewExchangeRates;
    decimals?: number;
    locale?: string;
  };
};

export type ConvertedCurrency = {
  raw: number;
  formatted: string;
  formattedWithoutText: string;
  currencyCode: string;
};

export const simpleFormatNumber = (
  value: number,
  locale = Locales.enUS,
  params: Intl.NumberFormatOptions = {
    style: "currency",
    currency: US_DOLLAR,
    currencyDisplay: "symbol",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  },
): string => {
  const cleanValue = !_.isNumber(value) || Number.isNaN(value) ? 0 : value;
  let formatted = new Intl.NumberFormat(locale, params).format(cleanValue / 100);

  if (locale !== Locales.enUS && formatted.includes("$")) {
    formatted = formatted.replace("$", `${params.currency} $`);
  }

  return formatted;
};

const convertCurrency = (
  value: number,
  exchangeRates: ReviewExchangeRates,
  from: string,
  to: string,
): number => {
  if (from !== to) {
    if (_.isNil(exchangeRates)) {
      throw new Error("Missing exchange rates for currency conversion");
    }

    const fromRate = exchangeRates[from]?.current;
    const toRate = exchangeRates[to]?.current;

    // boolean check covers all falsey values (null, undefined, 0, etc)
    if (!fromRate || !toRate) {
      const issueRate = !fromRate ? from : to;
      const isFrom = !fromRate;
      throw new Error(
        `Missing ${issueRate} exchange rate for currency conversion: ${isFrom ? "from" : "to"}`,
      );
    }

    if (to === US_DOLLAR) {
      return (value / fromRate) * 100;
    }

    if (from === US_DOLLAR) {
      return (value * toRate) / 100;
    }

    return (convertCurrency(value, exchangeRates, from, US_DOLLAR) * toRate) / 100;
  }

  return value;
};

export function getConvertedCurrency({ value, options }: ConvertCurrencyParams): ConvertedCurrency {
  const {
    sourceCurrency,
    targetCurrency,
    exchangeRates,
    decimals = 0,
    locale = Locales.enUS,
  } = options;

  const convertedValue = convertCurrency(value, exchangeRates, sourceCurrency, targetCurrency);

  const formattedValue = simpleFormatNumber(convertedValue, locale, {
    style: "currency",
    currency: targetCurrency || US_DOLLAR,
    currencyDisplay: locale === "en-US" ? "symbol" : "narrowSymbol",
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });

  const guardedFormattedValue =
    _.isEmpty(formattedValue) || _.isNil(formattedValue) ? String(convertedValue) : formattedValue;

  return {
    raw: convertedValue,
    formatted: guardedFormattedValue,
    formattedWithoutText: guardedFormattedValue.replace(/[a-zA-Z]/g, "").trim(),
    currencyCode: targetCurrency,
  };
}
